import React, { Component } from 'react';
import { PlayerInfo } from './content/PlayerInfo';
import HistoryOfEvents from './content/history-of-events/HistoryOfEvents';
import HistoryOfAlerts from './content/HistoryOfAlerts';
import { connect } from 'react-redux';
import {
    fetchPlayerAlerts,
    fetchPlayerDetails,
    fetchPlayerEvents
} from './content/redux/actions';
import {
    defaultFirstPage,
    defaultItemsPerPage
} from '../../../../services/Paging';
import { default as _, isEqual } from 'lodash';
import {
    getSandboxJurisdictionCodes,
    getSelectedBrandId,
    getSelectedJurisdictionCode,
    isSandboxSelected
} from '../../../../jurisdiction/redux/selectors';
import { makeRuleTypeDropdownOptions } from '../../../../shared/models/rule-type';
import { makeEventTypeDropdownOptions } from '../../../../shared/models/event-type';
import { getStatusAsBoolean } from '../../../../shared/models/status';
import PageHeaderWithBackButton from '../../../../shared/headers/PageHeaderWithBackButton';
import { exportCsvAlerts } from '../../export-csv/redux/actions';
import { getExportCsvLoading } from '../../export-csv/redux/selector';

class PlayerDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            eventsPagination: {
                pageSize: defaultItemsPerPage,
                page: defaultFirstPage
            },
            eventSort: {},
            eventFilters: {
                type: null
            },
            alertsPagination: {
                pageSize: defaultItemsPerPage,
                page: defaultFirstPage
            },
            alertFilters: {
                type: null,
                severity: null,
                status: null
            },
            dismissed: null,
            alertSort: {}
        };
    }

    makeAlertFiltersConfig = () => ({
        typeConfig: {
            label: 'Rule Type',
            options: makeRuleTypeDropdownOptions()
        },
        severityConfig: {},
        statusConfig: {}
    });

    makeEventFiltersConfig = () => ({
        typeConfig: {
            label: 'Event Type',
            options: makeEventTypeDropdownOptions()
        },
        severityConfig: null,
        statusConfig: null
    });

    componentDidMount() {
        this.fetchPlayerFullDetails();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            !isEqual(prevProps.jurisdictionCode, this.props.jurisdictionCode) ||
            !isEqual(prevProps.brandId, this.props.brandId)
        ) {
            this.fetchPlayerAlertsFromState();
            this.fetchPlayerEventsFromState();
        }
    }

    fetchPlayerEventsFromState() {
        const { location, jurisdictionCode, brandId } = this.props;
        const { type } = this.state.eventFilters;

        this.props.fetchPlayerEvents(
            location.state.playerId,
            this.state.eventsPagination,
            {
                sortType: this.state.eventSort.sortingKey,
                sortOrder: this.state.eventSort.sortDirection
            },
            jurisdictionCode,
            brandId,
            type
        );
    }

    fetchPlayerAlertsFromState() {
        const { jurisdictionCode, brandId } = this.props;
        const { type, severity, status } = this.state.alertFilters;

        this.props.fetchPlayerAlerts(
            this.props.location.state.playerId,
            jurisdictionCode,
            brandId,
            this.state.alertsPagination,
            {
                sortType: this.state.alertSort.sortingKey,
                sortOrder: this.state.alertSort.sortDirection
            },
            type,
            severity,
            // todo: Filtering by dismissType is not implemented on the BE.
            //  This is a temporary solution to make the status filtering work.
            getStatusAsBoolean(status)
        );
    }

    fetchPlayerFullDetails() {
        const {
            location,
            events,
            alerts,
            fetchPlayerDetails,
            fetchPlayerEvents,
            fetchPlayerAlerts,
            jurisdictionCode,
            brandId
        } = this.props;

        fetchPlayerDetails(location.state.playerId);
        fetchPlayerEvents(
            location.state.playerId,
            events.pagination,
            events.sort,
            jurisdictionCode,
            brandId
        );
        fetchPlayerAlerts(
            location.state.playerId,
            jurisdictionCode,
            brandId,
            alerts.pagination
        );
    }

    updateEventsPagination = pagination =>
        this.setState(
            {
                ...this.state,
                eventsPagination: pagination
            },
            () => this.fetchPlayerEventsFromState()
        );

    updateEventsSort = eventSort =>
        this.setState(
            {
                ...this.state,
                eventSort
            },
            () => this.fetchPlayerEventsFromState()
        );

    updateAlertsPagination = pagination =>
        this.setState(
            {
                ...this.state,
                alertsPagination: pagination
            },
            () => this.fetchPlayerAlertsFromState()
        );

    updateAlertsSort = alertSort =>
        this.setState(
            {
                ...this.state,
                alertSort
            },
            () => this.fetchPlayerAlertsFromState()
        );

    handleEventsApplyFilters = eventFilters =>
        this.setState(
            {
                ...this.state,
                eventsPagination: {
                    ...this.state.eventsPagination,
                    page: defaultFirstPage
                },
                eventFilters
            },
            () => this.fetchPlayerEventsFromState()
        );

    handleEventsClearFilters = eventFilters =>
        this.setState(
            {
                ...this.state,
                eventFilters
            },
            () => this.fetchPlayerEventsFromState()
        );

    handleEventsRemoveFilter = eventFilters =>
        this.setState(
            {
                ...this.state,
                eventFilters
            },
            () => this.fetchPlayerEventsFromState()
        );

    handleAlertsApplyFilters = alertFilters =>
        this.setState(
            {
                ...this.state,
                alertsPagination: {
                    ...this.state.alertsPagination,
                    page: defaultFirstPage
                },
                alertFilters
            },
            () => this.fetchPlayerAlertsFromState()
        );

    handleAlertsClearFilters = alertFilters =>
        this.setState(
            {
                ...this.state,
                alertFilters
            },
            () => this.fetchPlayerAlertsFromState()
        );

    handleAlertsRemoveFilter = alertFilters =>
        this.setState(
            {
                ...this.state,
                alertFilters
            },
            () => this.fetchPlayerAlertsFromState()
        );

    handleExportCsv = () => {
        const {
            exportCsvAlerts,
            player,
            jurisdictionCode,
            brandId
        } = this.props;
        const { alertsPagination, alertSort, alertFilters } = this.state;

        _.isFunction(exportCsvAlerts) &&
            exportCsvAlerts({
                dismissed: getStatusAsBoolean(
                    alertFilters && alertFilters.status
                ),
                pagination: alertsPagination,
                sort: alertSort,
                jurisdictionCode,
                brandId,
                ruleType: alertFilters && alertFilters.type,
                playerId: player.id,
                alertLevel: alertFilters && alertFilters.severity
            });
    };

    render() {
        const {
            player,
            events,
            alerts,
            loaded,
            exportCsvLoading,
            sandboxCodes,
            sandboxSelected
        } = this.props;
        // The alerts of a sandbox cannot be exported: of a sandbox that is selected, or of a player who is in one
        const exportDisabled =
            sandboxSelected ||
            _.includes(sandboxCodes, _.get(player, 'originalJurisdiction'));

        return (
            <div className="core-content-container container">
                <PageHeaderWithBackButton />
                <PlayerInfo player={player} loaded={loaded} />
                <HistoryOfEvents
                    events={events.data}
                    pagination={events.pagination}
                    loading={events.loading}
                    onPaginationUpdated={pagination =>
                        this.updateEventsPagination(pagination)
                    }
                    onSortUpdated={sort => this.updateEventsSort(sort)}
                    filters={this.state.eventFilters}
                    filtersConfig={this.makeEventFiltersConfig()}
                    applyFilters={this.handleEventsApplyFilters}
                    clearFilters={this.handleEventsClearFilters}
                    removeFilter={this.handleEventsRemoveFilter}
                />
                <HistoryOfAlerts
                    alerts={alerts.data}
                    pagination={{
                        ...this.state.alertsPagination,
                        hasNextPage: alerts.pagination.hasNextPage
                    }}
                    loading={alerts.loading}
                    onPaginationUpdated={pagination =>
                        this.updateAlertsPagination(pagination)
                    }
                    onSortUpdated={sort => this.updateAlertsSort(sort)}
                    filters={this.state.alertFilters}
                    filtersConfig={this.makeAlertFiltersConfig()}
                    applyFilters={this.handleAlertsApplyFilters}
                    clearFilters={this.handleAlertsClearFilters}
                    removeFilter={this.handleAlertsRemoveFilter}
                    handleExportCsv={this.handleExportCsv}
                    exportCsvLoading={exportCsvLoading}
                    exportDisabled={exportDisabled}
                />
            </div>
        );
    }
}

const mapStateToProps = state => {
    const { data, events, alerts, loaded, loading, error } = state.player;
    const jurisdictionCode = getSelectedJurisdictionCode(state);
    const brandId = getSelectedBrandId(state);
    const exportCsvLoading = getExportCsvLoading(state);

    return {
        jurisdictionCode,
        brandId,
        player: data,
        events,
        alerts,
        loaded,
        loading,
        error,
        exportCsvLoading,
        sandboxCodes: getSandboxJurisdictionCodes(state),
        sandboxSelected: isSandboxSelected(state)
    };
};
const mapDispatchToProps = dispatch => ({
    fetchPlayerDetails: playerId => dispatch(fetchPlayerDetails(playerId)),
    fetchPlayerEvents: (
        playerId,
        pagination,
        sort,
        jurisdictionCode,
        brandId,
        type
    ) =>
        dispatch(
            fetchPlayerEvents({
                playerId,
                pagination,
                sort,
                jurisdictionCode,
                brandId,
                eventType: type
            })
        ),
    fetchPlayerAlerts: (
        playerId,
        jurisdictionCode,
        brandId,
        pagination,
        sort,
        type,
        severity,
        dismissed
    ) =>
        dispatch(
            fetchPlayerAlerts({
                playerId,
                jurisdictionCode,
                brandId,
                pagination,
                sort,
                ruleType: type,
                alertLevel: severity,
                dismissed
            })
        ),
    exportCsvAlerts: params => dispatch(exportCsvAlerts(params))
});

export default connect(mapStateToProps, mapDispatchToProps)(PlayerDetails);
